// RelationshipPipelineTable — the personal CRM's relationship pipeline: one
// row per contact, real organisation/project/follow-up data, edit + delete.
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Trash2, Pencil } from 'lucide-react'
import { deleteCrmContact as deleteCrmContactApi } from '../../services/api'
import type { CrmContact, CrmTodo, Project } from '../../types'
import {
  CONTACT_STATUS_BADGE,
  CONTACT_STATUS_LABEL,
  contactDisplayName,
  contactInitials,
  contactStatus,
  formatTimestamp,
  lastTouch,
  openTodosForContact,
  projectNameFor,
} from '../../utils/crmPipeline'
import EmptyState from '../ui/EmptyState'
import ConfirmDialog from '../ui/ConfirmDialog'

interface RelationshipPipelineTableProps {
  contacts: CrmContact[]
  todos: CrmTodo[]
  projects: Project[]
  onContactDeleted: (contactId: string) => void
  onAddContact: () => void
  /** Injectable for tests (defaults to the real API call) — avoids mocking
   * the services/api module entirely; tests pass a vi.fn() here instead. */
  deleteCrmContact?: typeof deleteCrmContactApi
}

export default function RelationshipPipelineTable({
  contacts,
  todos,
  projects,
  onContactDeleted,
  onAddContact,
  deleteCrmContact = deleteCrmContactApi,
}: RelationshipPipelineTableProps) {
  const navigate = useNavigate()
  const [pendingDelete, setPendingDelete] = useState<CrmContact | null>(null)

  const confirmDelete = async () => {
    if (!pendingDelete) return
    await deleteCrmContact(pendingDelete.contact_id, pendingDelete.project_id)
    onContactDeleted(pendingDelete.contact_id)
  }

  if (contacts.length === 0) {
    return (
      <div className="light-card p-4">
        <EmptyState
          icon={Users}
          title="No contacts yet"
          description="People you add to a project's CRM appear here as a relationship pipeline. Add your first contact to start tracking follow-ups."
          action={
            <button
              onClick={onAddContact}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600"
            >
              + Add contact
            </button>
          }
        />
      </div>
    )
  }

  return (
    <div className="light-card overflow-hidden">
      <div className="px-4 py-3 border-b border-sky-200/50">
        <h3 className="text-sm font-semibold text-gray-900">Relationship pipeline</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-sky-200/50">
          <thead className="bg-sky-500/5">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organisation</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked project</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last touch</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To-dos</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sky-200/30">
            {contacts.map((c) => {
              // BUG-VIS-4-1: /crm/my-contacts legitimately returns contacts
              // with no first_name/last_name (org-only rows, directory-copy
              // imports, ai_ecosystem_match suggestions). Naively
              // interpolating `${first_name} ${last_name}` rendered the
              // literal string "undefined undefined" for those — use the
              // safe fallback chain (name -> organisation -> email -> plain
              // label) instead, and NEVER render "undefined".
              const fullName = contactDisplayName(c)
              // LIVE-DATA fix: never print "No details" — show the real name
              // always, and only add a sub-line when there's a real
              // role/title to show. A contact with neither still reads fine
              // with just its name.
              const subLine = [c.title, c.role].filter(Boolean).join(' — ')
              const status = contactStatus(todos, c.contact_id)
              const openCount = openTodosForContact(todos, c.contact_id).length
              const touch = formatTimestamp(lastTouch(c, todos))
              const projectName = projectNameFor(projects, c.project_id)
              const detailHref = `/project/${c.project_id}/crm/contacts/${c.contact_id}`

              return (
                <tr key={c.contact_id} className="hover:bg-white/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-sky-500/10 rounded-full flex items-center justify-center text-xs font-semibold text-sky-700 shrink-0">
                        {contactInitials(c)}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-gray-900 truncate">{fullName}</span>
                          {c.source === 'author-conversion' && (
                            <span className="text-xs bg-amber-500/20 text-amber-700 px-2 py-0.5 rounded-full shrink-0">Author</span>
                          )}
                        </div>
                        {subLine && <p className="text-xs text-gray-500 truncate">{subLine}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{c.organisation_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${CONTACT_STATUS_BADGE[status]}`}>
                      {CONTACT_STATUS_LABEL[status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {projectName ? (
                      <span className="inline-flex items-center text-xs bg-sky-500/10 text-sky-700 px-2 py-0.5 rounded-full">{projectName}</span>
                    ) : (
                      <span className="text-xs text-gray-500 font-mono">{c.project_id}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{touch || '—'}</td>
                  <td className="px-4 py-3">
                    {openCount > 0 ? (
                      <span className="text-xs bg-sky-500/10 text-sky-700 px-2 py-0.5 rounded-full font-medium">{openCount} open</span>
                    ) : (
                      <span className="text-xs text-gray-500">None</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => navigate(detailHref)}
                        className="text-gray-500 hover:text-sky-600 transition-colors"
                        title="Edit contact"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setPendingDelete(c)}
                        className="text-gray-300 hover:text-red-500 transition-colors"
                        title="Delete contact"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title="Delete contact?"
        message={
          pendingDelete
            ? `This permanently removes ${contactDisplayName(pendingDelete)} from its project. This can't be undone.`
            : ''
        }
        confirmLabel="Delete contact"
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  )
}
